/**
 * A region's workshops as a calendar on a phone.
 *
 * The grid marks each workshop with a coloured bar instead of a chip, and the
 * day a viewer taps is listed underneath. The calendar's own List view is left
 * out, since the List / Calendar switch above already offers one.
 */
import { useState, type CSSProperties } from 'react';

import type { CalendarState } from '../../primitives/use-calendar-state';
import { utcDayKey } from '../../primitives/workshops-calendar-dates';
import type { CalendarView, WorkshopChip } from '../../primitives/workshopsCalendarTypes';

import { PhoneCalendarDay } from './PhoneCalendarDay';
import { PhoneCalendarMarker } from './PhoneCalendarMarker';
import { useOpenWorkshop, withoutViewedRegionLabel } from './region-workshops-shared';
import { RegionWorkshopsCalendar } from './RegionWorkshopsCalendar';

export interface RegionWorkshopsPhoneCalendarProps {
  /** The region's full ancestor path. */
  path: string[];
  calendar: CalendarState;
  views: CalendarView[];
}

/** Room for the date and a few markers, so a whole month fits on screen with
 *  the picked day still in view beneath it. */
const SHORT_MONTH_ROWS = { '--nb-cal-month-row': '72px' } as CSSProperties;

export function RegionWorkshopsPhoneCalendar({
  path,
  calendar,
  views,
}: RegionWorkshopsPhoneCalendarProps) {
  const openWorkshop = useOpenWorkshop();
  const [picked, setPicked] = useState<{ day: string; workshops: WorkshopChip[] } | null>(null);

  return (
    <>
      <div style={SHORT_MONTH_ROWS}>
        <RegionWorkshopsCalendar
          path={path}
          calendar={calendar}
          views={views}
          renderWorkshop={PhoneCalendarMarker}
          onDayClick={(date, dayWorkshops) =>
            setPicked({
              day: utcDayKey(date),
              workshops: withoutViewedRegionLabel(dayWorkshops, path[path.length - 1]),
            })
          }
        />
      </div>
      <PhoneCalendarDay
        day={picked?.day ?? null}
        workshops={picked?.workshops ?? []}
        onWorkshopClick={openWorkshop}
      />
    </>
  );
}
